/**
 * @license
 * Copyright 2026 okayest! studios LLC. All Rights Reserved.
 */

import { LitElement, css, html } from 'lit';
import { customElement, query, state } from 'lit/decorators.js';
import WaDialog from '@awesome.me/webawesome/dist/components/dialog/dialog.js';

import '@awesome.me/webawesome/dist/components/button/button.js';
import '@awesome.me/webawesome/dist/components/dialog/dialog.js';
import '@awesome.me/webawesome/dist/components/icon/icon.js';
import './counter-edit-dialog';
import './counter-row';
import './counter-view';
import './export-dialog';
import { CounterEditDialog } from './counter-edit-dialog';
import { ExportDialog } from './export-dialog';
import { RowCounter, RowCounterState, getCount, getCounter, getRepeats } from '../data/counter-model';
import { addCounterListener, getCounterState, removeCounterListener } from '../data/counter-store';
import { downloadYarnlyExport } from '../data/yarnly-export';

/**
 * Row counter tool. A single counter is shown in the large view; multiple
 * counters are shown as a list, with the large view opening in a dialog.
 */
@customElement('yl-row-counter')
export class RowCounterElement extends LitElement {
  static styles = css`
    :host {
      display: block;
    }

    section {
      border-radius: 24px;
      background: var(--yl-surface);
      padding: 16px;
    }

    .header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      min-height: 36px;
      margin: 0 4px 8px 8px;
    }

    .header h2 {
      margin: 0;
      font-size: 1.1rem;
      font-weight: 500;
    }

    .subcounters {
      border-left: 8px solid transparent;
      margin-left: 4px;
      border-radius: 4px;
    }

    .group + .group {
      border-top: 1px solid var(--yl-divider);
      margin-top: 4px;
      padding-top: 4px;
    }

    .footer {
      display: flex;
      justify-content: space-between;
      gap: 8px;
      margin-top: 16px;
      padding-top: 16px;
      border-top: 1px solid var(--yl-divider);
    }

    .empty {
      text-align: center;
      padding: 24px 0;
      color: var(--yl-secondary-text);
    }

    .counter-dialog {
      --width: 26rem;
    }
  `;

  @state()
  private _state: RowCounterState = getCounterState();

  @state()
  private _openCounterId: string | null = null;

  @query('yl-counter-edit-dialog')
  private _editDialog!: CounterEditDialog;

  @query('yl-export-dialog')
  private _exportDialog!: ExportDialog;

  @query('.counter-dialog')
  private _counterDialog!: WaDialog;

  private onCountersChangedCallback = this.onCountersChanged.bind(this);

  private onCountersChanged(state: RowCounterState) {
    this._state = state;
    const openId = this._openCounterId;
    if (openId !== null && (getCounter(state, openId) === null || state.counters.length < 2)) {
      this._counterDialog.open = false;
    }
  }

  connectedCallback() {
    super.connectedCallback();
    this._state = getCounterState();
    addCounterListener(this.onCountersChangedCallback);
  }

  disconnectedCallback() {
    removeCounterListener(this.onCountersChangedCallback);
    super.disconnectedCallback();
  }

  private _openCounter(e: CustomEvent<string>) {
    this._openCounterId = e.detail;
    this._counterDialog.open = true;
  }

  private _editCounter(e: CustomEvent<string>) {
    this._editDialog.show(e.detail);
  }

  private _export() {
    const fileName = downloadYarnlyExport(this._state);
    this._exportDialog.show(fileName);
  }

  private _renderRow(counter: RowCounter) {
    const state = this._state;
    return html`<yl-counter-row
      counter-id=${counter.id}
      label=${counter.label || 'Untitled counter'}
      count=${getCount(state, counter.id)}
      ?repeating=${counter.rowsInRepeat !== null}
      repeats=${getRepeats(state, counter.id)}
      ?highlight=${counter.id === state.lastUsedCounterId}
    ></yl-counter-row>`;
  }

  private _renderList() {
    const state = this._state;
    const topLevel = state.counters.filter((c) => c.parentId === null);
    if (topLevel.length === 0) {
      return html`<div class="empty">No counters yet. Add one to start counting.</div>`;
    }
    return topLevel.map((parent) => {
      const children = state.counters.filter((c) => c.parentId === parent.id);
      return html`<div class="group">
        ${this._renderRow(parent)}
        ${children.length > 0
          ? html`<div class="subcounters">${children.map((c) => this._renderRow(c))}</div>`
          : null}
      </div>`;
    });
  }

  render() {
    const state = this._state;
    const single = state.counters.length === 1 ? state.counters[0] : null;
    const openCounter = this._openCounterId !== null ? getCounter(state, this._openCounterId) : null;

    return html`
      <section aria-label=${single?.label || 'Row counters'} @open-counter=${this._openCounter} @edit-counter=${this._editCounter}>
        ${single !== null
          ? html`<div class="header"><h2>${single.label || 'Row Counter'}</h2></div>
              <yl-counter-view hide-title counter-id=${single.id}></yl-counter-view>`
          : this._renderList()}
        <div class="footer">
          <wa-button size="s" appearance="outlined" pill ?disabled=${state.counters.length === 0} @click=${this._export}>
            <wa-icon slot="start" name="download"></wa-icon>
            Export
          </wa-button>
          <wa-button size="s" appearance="filled" pill @click=${() => this._editDialog.show(null)}>
            <wa-icon slot="start" name="plus"></wa-icon>
            Counter
          </wa-button>
        </div>

        <wa-dialog
          class="counter-dialog"
          label=${openCounter?.label || 'Row Counter'}
          light-dismiss
          @wa-after-hide=${(e: Event) => e.target === this._counterDialog && (this._openCounterId = null)}
        >
          ${openCounter !== null
        ? html`<yl-counter-view hide-title counter-id=${openCounter.id}></yl-counter-view>`
        : null}
        </wa-dialog>
      </section>

      <yl-counter-edit-dialog></yl-counter-edit-dialog>
      <yl-export-dialog></yl-export-dialog>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'yl-row-counter': RowCounterElement;
  }
}
